import React from 'react';
import { Container, Typography, Box, Paper, Divider, List, ListItem, ListItemText } from '@mui/material';
import SEO from '../components/SEO';

const ChildSafetyStandardsPage = () => {
  return (
    <>
      <SEO
        title="Child Safety Standards - Couplendar"
        description="Couplendar's standards against child sexual abuse and exploitation (CSAE), and how to report a concern."
        url="https://couplendar.com/child-safety-standards"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Child Safety Standards
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ '& > *': { mb: 3 } }}>
            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                Zero-tolerance policy
              </Typography>
              <Typography variant="body1" paragraph>
                Couplendar has a zero-tolerance policy toward child sexual abuse and exploitation (CSAE) in any form. This applies to all content, communication, and conduct on our platform, without exception.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                How Couplendar is designed to prevent CSAE
              </Typography>
              <Typography variant="body1" paragraph>
                Couplendar is a private relationship-tracking app for two consenting adult partners. By design, it does not include features that are commonly exploited for CSAE:
              </Typography>
              <List sx={{ listStyleType: 'disc', pl: 4, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem>
                  <ListItemText primary="No public profiles, feeds, or content discoverable by strangers." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="No messaging, chat, or contact with users outside a single, mutually-linked relationship pairing." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Relationships are only formed when both people explicitly link accounts using a private pairing code." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="User-uploaded images are limited to profile and relationship pictures, visible only to the linked partner." />
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                Use of Couplendar is restricted to users who meet the minimum age requirement stated in our{' '}
                <a href="/terms-of-service">Terms of Service</a>. The app is not directed at children and is not designed, marketed, or intended for use by minors.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                Reporting a concern
              </Typography>
              <Typography variant="body1" paragraph>
                In the mobile app, go to <strong>Profile → Report a Safety Concern</strong> to submit a report directly from within the app. You can also email{' '}
                <a href="mailto:support@couplendar.com">support@couplendar.com</a> at any time. Include as much detail as possible (account, date/time, and a description of what you observed).
              </Typography>
              <Typography variant="body1" paragraph>
                We investigate every report promptly, remove violating content and accounts, and, where legally required, report confirmed CSAE to the National Center for Missing & Exploited Children (NCMEC) and cooperate with law enforcement.
              </Typography>
              <Typography variant="body1" paragraph>
                If a child is in immediate danger, contact your local law enforcement or emergency services first.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                Contact
              </Typography>
              <Typography variant="body1" paragraph>
                Questions about these standards or our compliance practices can be directed to{' '}
                <a href="mailto:support@couplendar.com">support@couplendar.com</a>. See also our{' '}
                <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms-of-service">Terms of Service</a>.
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ChildSafetyStandardsPage;
